import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Filter, Search, X, Edit, Trash2 } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { apiFetch } from '../lib/api';

const Tasks = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [actions, setActions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [filteredActions, setFilteredActions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterProject, setFilterProject] = useState('all');
  const [filterPhase, setFilterPhase] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [newAction, setNewAction] = useState({
    project_id: '',
    project_name: '',
    phase: '',
    title: '',
    description: '',
    assignee: '',
    priority: 'Moyenne',
    status: 'À faire',
    due_date: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [actions, searchQuery, filterProject, filterPhase, filterStatus]);

  const fetchData = async () => {
    try {
      const [actionsRes, projectsRes, teamRes] = await Promise.all([
        apiFetch('/api/actions'),
        apiFetch('/api/projects'),
        apiFetch('/api/team')
      ]);
      
      const actionsData = await actionsRes.json();
      const projectsData = await projectsRes.json();
      const teamData = await teamRes.json();
      
      setActions(actionsData);
      setProjects(projectsData);
      setTeamMembers(teamData);
      
      // Check if we need to open a specific action from URL params
      const actionId = searchParams.get('action');
      if (actionId) {
        const action = actionsData.find(a => a.id === actionId);
        if (action) {
          setSelectedAction(action);
          setIsEditDialogOpen(true);
          // Clear the URL param
          setSearchParams({});
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...actions];

    if (searchQuery) {
      filtered = filtered.filter(action =>
        action.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        action.project_name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (filterProject !== 'all') {
      filtered = filtered.filter(action => action.project_id === filterProject);
    }

    if (filterPhase !== 'all') {
      filtered = filtered.filter(action => action.phase === filterPhase);
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(action => action.status === filterStatus);
    }

    setFilteredActions(filtered);
  };

  const handleAddAction = async () => {
    try {
      const response = await apiFetch('/api/actions', {
        method: 'POST',
        body: JSON.stringify(newAction)
      });
      
      if (response.ok) {
        await fetchData();
        setIsAddDialogOpen(false);
        resetNewAction();
      }
    } catch (error) {
      console.error('Error adding action:', error);
    }
  };

  const handleDeleteAction = async (actionId) => {
    if (!window.confirm('Supprimer cette action ?')) return;
    
    try {
      const response = await apiFetch(`/api/actions/${actionId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        await fetchData();
      }
    } catch (error) {
      console.error('Error deleting action:', error);
    }
  };

  const handleEditAction = (action) => {
    setSelectedAction(action);
    setIsEditDialogOpen(true);
  };

  const handleUpdateAction = async () => {
    if (!selectedAction) return;
    
    try {
      const response = await apiFetch(`/api/actions/${selectedAction.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          title: selectedAction.title,
          description: selectedAction.description,
          assignee: selectedAction.assignee,
          priority: selectedAction.priority,
          status: selectedAction.status,
          due_date: selectedAction.due_date
        })
      });
      
      if (response.ok) {
        await fetchData();
        setIsEditDialogOpen(false);
        setSelectedAction(null);
      }
    } catch (error) {
      console.error('Error updating action:', error);
    }
  };

  const handleStatusChange = async (actionId, newStatus) => {
    try {
      const response = await apiFetch(`/api/actions/${actionId}`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        await fetchData();
      }
    } catch (error) {
      console.error('Error updating action:', error);
    }
  };

  const resetNewAction = () => {
    setNewAction({
      project_id: '',
      project_name: '',
      phase: '',
      title: '',
      description: '',
      assignee: '',
      priority: 'Moyenne',
      status: 'À faire',
      due_date: ''
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Haute': return 'bg-red-100 text-red-700';
      case 'Moyenne': return 'bg-yellow-100 text-yellow-700';
      case 'Basse': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'À faire': return 'bg-gray-100 text-gray-700';
      case 'En cours': return 'bg-blue-100 text-blue-700';
      case 'En attente client': return 'bg-orange-100 text-orange-700';
      case 'En validation': return 'bg-purple-100 text-purple-700';
      case 'Terminé': return 'bg-green-100 text-green-700';
      case 'Annulé': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const phases = [
    'RDV conception', 'Envoi framework', 'Choix NDD', 'Installation Wordpress',
    'Arborescences et plugin', 'Conception design', 'Livraison page accueil',
    'Validation client', 'Intégration contenus client', 'Conception site',
    'SEO', 'Mise en ligne', 'Suivi'
  ];

  const statuses = ['À faire', 'En cours', 'En attente client', 'En validation', 'Terminé', 'Annulé'];
  const priorities = ['Haute', 'Moyenne', 'Basse'];

  return (
    <div className="space-y-6" data-testid="tasks-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight" data-testid="page-title">
            Actions Projet
          </h1>
          <p className="text-base text-muted-foreground mt-2">
            Gérez les actions liées à vos projets
          </p>
        </div>
        <Button onClick={() => setIsAddDialogOpen(true)} data-testid="add-action-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle action
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-4" data-testid="filters-section">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="search-input"
            />
          </div>
          
          <Select value={filterProject} onValueChange={setFilterProject}>
            <SelectTrigger data-testid="filter-project">
              <SelectValue placeholder="Tous les projets" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les projets</SelectItem>
              {projects.map(project => (
                <SelectItem key={project.id} value={project.id}>
                  {project.client_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterPhase} onValueChange={setFilterPhase}>
            <SelectTrigger data-testid="filter-phase">
              <SelectValue placeholder="Toutes les phases" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les phases</SelectItem>
              {phases.map(phase => (
                <SelectItem key={phase} value={phase}>{phase}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger data-testid="filter-status">
              <SelectValue placeholder="Tous les statuts" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              {statuses.map(status => (
                <SelectItem key={status} value={status}>{status}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Actions Table */}
      <Card data-testid="actions-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Projet</TableHead>
              <TableHead>Phase</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Responsable</TableHead>
              <TableHead>Priorité</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead>Date limite</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8">
                  Chargement...
                </TableCell>
              </TableRow>
            ) : filteredActions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                  Aucune action trouvée
                </TableCell>
              </TableRow>
            ) : (
              filteredActions.map((action, idx) => (
                <TableRow key={action.id} data-testid={`action-row-${idx}`}>
                  <TableCell className="font-medium">
                    <button
                      onClick={() => navigate(`/project/${action.project_id}`)}
                      className="hover:underline text-left"
                    >
                      {action.project_name}
                    </button>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{action.phase}</Badge>
                  </TableCell>
                  <TableCell>{action.title}</TableCell>
                  <TableCell>{action.assignee}</TableCell>
                  <TableCell>
                    <Badge className={getPriorityColor(action.priority)}>
                      {action.priority}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Select
                      value={action.status}
                      onValueChange={(value) => handleStatusChange(action.id, value)}
                    >
                      <SelectTrigger className="w-[160px]">
                        <Badge className={getStatusColor(action.status)}>
                          {action.status}
                        </Badge>
                      </SelectTrigger>
                      <SelectContent>
                        {statuses.map(status => (
                          <SelectItem key={status} value={status}>{status}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>{action.due_date}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditAction(action)}
                        data-testid={`edit-action-${idx}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteAction(action.id)}
                        data-testid={`delete-action-${idx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Add Action Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="add-action-dialog">
          <DialogHeader>
            <DialogTitle>Nouvelle action</DialogTitle>
            <DialogDescription>
              Créer une nouvelle action pour un projet
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Projet *</label>
                <Select
                  value={newAction.project_id}
                  onValueChange={(value) => {
                    const project = projects.find(p => p.id === value);
                    setNewAction({
                      ...newAction,
                      project_id: value,
                      project_name: project?.client_name || ''
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un projet" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map(project => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.client_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Phase *</label>
                <Select
                  value={newAction.phase}
                  onValueChange={(value) => setNewAction({ ...newAction, phase: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner une phase" />
                  </SelectTrigger>
                  <SelectContent>
                    {phases.map(phase => (
                      <SelectItem key={phase} value={phase}>{phase}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Titre *</label>
              <Input
                value={newAction.title}
                onChange={(e) => setNewAction({ ...newAction, title: e.target.value })}
                placeholder="Ex: Intégrer le formulaire de contact"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Description</label>
              <Textarea
                value={newAction.description}
                onChange={(e) => setNewAction({ ...newAction, description: e.target.value })}
                placeholder="Description détaillée de l'action"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Responsable *</label>
                <Select
                  value={newAction.assignee}
                  onValueChange={(value) => setNewAction({ ...newAction, assignee: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un responsable" />
                  </SelectTrigger>
                  <SelectContent>
                    {teamMembers.map(member => (
                      <SelectItem key={member.id} value={member.name}>
                        {member.name} - {member.role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Date limite *</label>
                <Input
                  type="date"
                  value={newAction.due_date}
                  onChange={(e) => setNewAction({ ...newAction, due_date: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Priorité</label>
                <Select
                  value={newAction.priority}
                  onValueChange={(value) => setNewAction({ ...newAction, priority: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {priorities.map(priority => (
                      <SelectItem key={priority} value={priority}>{priority}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Statut</label>
                <Select
                  value={newAction.status}
                  onValueChange={(value) => setNewAction({ ...newAction, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statuses.map(status => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleAddAction}
              disabled={!newAction.project_id || !newAction.phase || !newAction.title || !newAction.assignee || !newAction.due_date}
            >
              Créer l'action
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Action Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Détails de l'action</DialogTitle>
            <DialogDescription>
              Consultez et modifiez les informations de cette action
            </DialogDescription>
          </DialogHeader>

          {selectedAction && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Titre</label>
                <Input
                  value={selectedAction.title}
                  onChange={(e) => setSelectedAction({ ...selectedAction, title: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Description</label>
                <Textarea
                  value={selectedAction.description || ''}
                  onChange={(e) => setSelectedAction({ ...selectedAction, description: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Projet</label>
                  <p className="text-sm p-2 bg-muted rounded-md">{selectedAction.project_name}</p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Phase</label>
                  <p className="text-sm p-2 bg-muted rounded-md">{selectedAction.phase}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Responsable</label>
                  <Select
                    value={selectedAction.assignee}
                    onValueChange={(value) => setSelectedAction({ ...selectedAction, assignee: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {teamMembers.map(member => (
                        <SelectItem key={member.id} value={member.name}>
                          {member.name} - {member.role}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Date limite</label>
                  <Input
                    type="date"
                    value={selectedAction.due_date}
                    onChange={(e) => setSelectedAction({ ...selectedAction, due_date: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Priorité</label>
                  <Select
                    value={selectedAction.priority}
                    onValueChange={(value) => setSelectedAction({ ...selectedAction, priority: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {priorities.map(priority => (
                        <SelectItem key={priority} value={priority}>{priority}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Statut</label>
                  <Select
                    value={selectedAction.status}
                    onValueChange={(value) => setSelectedAction({ ...selectedAction, status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statuses.map(status => (
                        <SelectItem key={status} value={status}>{status}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {selectedAction.auto_generated && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
                  Cette action a été générée automatiquement lors d'un changement de phase du projet.
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsEditDialogOpen(false);
              setSelectedAction(null);
            }}>
              Fermer
            </Button>
            <Button onClick={handleUpdateAction}>
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Tasks;
